import React from 'react';
import styled from 'styled-components';

import { Link } from 'components/core';

import { useBlurhashDataUrl } from './use-blurhash-data-url';
import { overlayButtonStyles } from './buy-it-again-shared-styles';

type BuyItAgainPromoCardProps = {
  href: string;
  onClick: () => void;
  logoImage?: string | null;
};

export function BuyItAgainPromoCard({ href, onClick, logoImage }: BuyItAgainPromoCardProps): JSX.Element {
  const blurredLogoDataUrl = useBlurhashDataUrl(logoImage);

  return (
    <CardShell data-testid='buy-it-again-promo-card'>
      {blurredLogoDataUrl && <PromoImage src={blurredLogoDataUrl} alt='' data-testid='buy-it-again-promo-image' />}

      <TextOverlay>
        <Heading>Reorder your favorites</Heading>
        <CtaLink href={href} onClick={onClick}>
          Past orders
        </CtaLink>
      </TextOverlay>
    </CardShell>
  );
}

// No hover treatment here — unlike the tiles beside it, the card body isn't itself a link
// (only the "Past orders" CTA is), so it shouldn't imply whole-card interactivity.
const CardShell = styled.div`
  background: var(--dcm-buy-it-again-promo-bg, ${({ theme }) => theme.colors.grey[95]});
  border: 1px solid var(--dcm-buy-it-again-group-border-color, #e3e7e9);
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  height: 152px;
  overflow: hidden;
  position: relative;
  width: 300px;

  @media screen and (min-width: 1110px) {
    height: 198px;
    width: 380px;
  }
`;

const PromoImage = styled.img`
  display: block;
  height: 100%;
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 100%;
`;

// Base background assumes NO backdrop-filter support (higher-opacity flat tint, for contrast
// on its own). Browsers that do support it get the lighter, softer glass look instead — the
// blur itself provides the separation from the blurred logo image behind it.
const TextOverlay = styled.div`
  align-items: flex-start;
  background-color: var(--dcm-buy-it-again-promo-glass-bg, rgba(255, 255, 255, 0.22));
  border: 1px solid var(--dcm-buy-it-again-promo-glass-border, rgba(255, 255, 255, 0.15));
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  justify-content: center;
  left: 0;
  padding: 20px;
  position: absolute;
  right: 0;
  top: 0;

  @supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
    background-color: var(--dcm-buy-it-again-promo-glass-bg-blurred, rgba(255, 255, 255, 0.08));
    backdrop-filter: blur(2px) saturate(105%);
    -webkit-backdrop-filter: blur(2px) saturate(105%);
  }
`;

const Heading = styled.p`
  color: var(--dcm-buy-it-again-promo-heading-color, #121516);
  font-size: 20px;
  font-weight: 800;
  line-height: ${24 / 20};
  max-width: 190px;
  text-shadow: var(--dcm-buy-it-again-promo-heading-text-shadow, 0 1px 2px rgba(0, 0, 0, 0.1));

  @media screen and (min-width: 1110px) {
    font-size: 24px;
  }
`;

// Styled directly on the anchor (not a nested span) so hover/focus land on the real interactive
// element — same solid-white pill look as the carousel's other overlay buttons (quick-add,
// "View similar"), just inline instead of absolutely positioned.
const CtaLink = styled(Link)`
  ${overlayButtonStyles}
  border-radius: 9999px;
  color: var(--dcm-filter-text-color, #121516);
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 16px;
`;
